import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Popup, ZoomControl, CircleMarker, Polyline, Polygon, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Globe, Search, X,
  Droplets, Mountain, Anchor, Building2, MapPin,
  Waves, Sun, Route, Landmark,
  Play, Pause, Clock, ChevronLeft, ChevronRight, Calendar, Maximize2, Minimize2,
  Camera,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { useSearchParams } from 'react-router-dom';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_STYLES = {
  capital:     { color: '#DC2626', icon: Building2, label: 'Capital',      radius: 5 },
  city:        { color: '#2563EB', icon: Building2, label: 'City',         radius: 4 },
  river:       { color: '#2563EB', icon: Droplets,  label: 'River',        radius: 4 },
  lake:        { color: '#0891B2', icon: Droplets,  label: 'Lake',         radius: 5 },
  ocean:       { color: '#1E3A5F', icon: Anchor,    label: 'Ocean',        radius: 7 },
  sea:         { color: '#6366F1', icon: Anchor,    label: 'Sea',          radius: 6 },
  mountain:    { color: '#78350F', icon: Mountain,  label: 'Mountain',     radius: 6 },
  desert:      { color: '#B45309', icon: Sun,       label: 'Desert',       radius: 6 },
  strait:      { color: '#0E7490', icon: Waves,     label: 'Strait',       radius: 6 },
  trade_route: { color: '#7C3AED', icon: Route,     label: 'Trade Route',  radius: 7 },
  region:      { color: '#166534', icon: MapPin,    label: 'Region',       radius: 6 },
  landmark:    { color: '#B91C1C', icon: Landmark,  label: 'Landmark',     radius: 5 },
  event:       { color: '#DC2626', icon: Clock,     label: 'Event',        radius: 6 },
  front_line:  { color: '#8B0000', icon: Route,     label: 'Front Line',   radius: 3 },
  empire:      { color: '#7C2D12', icon: Landmark,  label: 'Empire',       radius: 3 },
};

const PERIOD_STYLES = {
  'Imperialism':    { color: '#7C2D12', label: 'Imperialism (1870-1914)', startYear: 1870, endYear: 1914 },
  'WWI':            { color: '#8B4513', label: 'WWI (1914-1918)',         startYear: 1914, endYear: 1918 },
  'WWII':           { color: '#1F2937', label: 'WWII (1939-1945)',        startYear: 1939, endYear: 1945 },
  'Cold War':       { color: '#B91C1C', label: 'Cold War (1946-1991)',    startYear: 1946, endYear: 1991 },
  'Post-Cold War':  { color: '#0F766E', label: 'Post-Cold War (1991-)',   startYear: 1991, endYear: 2025 },
};

const GRADE_FILTERS = [
  { key: 'all',    label: 'All',      color: '#14213D' },
  { key: '7',      label: 'Grade 7 · Kansas', color: '#B45309' },
  { key: '10',     label: 'Grade 10 · World', color: '#1E40AF' },
];

const CONTINENTS = [
  { name: 'World',          lat: 20,    lng: 0,     zoom: 2 },
  { name: 'Kansas',         lat: 38.5,  lng: -98.5, zoom: 7 },
  { name: 'North America',  lat: 40,    lng: -98,   zoom: 4 },
  { name: 'South America',  lat: -15,   lng: -60,   zoom: 3 },
  { name: 'Europe',         lat: 50,    lng: 15,    zoom: 4 },
  { name: 'Africa',         lat: 5,     lng: 20,    zoom: 3 },
  { name: 'Asia',           lat: 35,    lng: 90,    zoom: 3 },
  { name: 'Middle East',    lat: 28,    lng: 45,    zoom: 5 },
  { name: 'Oceania',        lat: -25,   lng: 140,   zoom: 4 },
];

const TILE_LAYERS = {
  political: { url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', attribution: '&copy; OSM &copy; CARTO', label: 'Political' },
  physical:  { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Physical_Map/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri', label: 'Physical' },
  satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri', label: 'Satellite' },
  terrain:   { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenTopoMap', label: 'Terrain' },
};

function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => { map.flyTo(center, zoom, { duration: 1 }); }, [center, zoom, map]);
  return null;
}

export default function GeographyMap() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [activeTypes, setActiveTypes] = useState(new Set(Object.keys(FEATURE_STYLES)));
  const [tileLayer, setTileLayer] = useState('political');
  const [mapCenter, setMapCenter] = useState([20, 0]);
  const [mapZoom, setMapZoom] = useState(2);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [stats, setStats] = useState({});
  // Timeline state
  const [timelineActive, setTimelineActive] = useState(null); // null | 'WWI' | 'WWII' | 'Cold War'
  const [currentYear, setCurrentYear] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playIntervalRef = useRef(null);
  // Present-mode state (query param `?present=1`)
  const [searchParams, setSearchParams] = useSearchParams();
  const presentMode = searchParams.get('present') === '1';

  // Enter/exit real browser fullscreen when present mode toggles
  useEffect(() => {
    if (presentMode) {
      // Best-effort request; some browsers require a user gesture to grant.
      const el = document.documentElement;
      if (el.requestFullscreen && !document.fullscreenElement) {
        el.requestFullscreen().catch(() => {});
      }
    } else if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }, [presentMode]);

  // ESC to exit present mode
  useEffect(() => {
    if (!presentMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSearchParams({});
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [presentMode, setSearchParams]);

  const enterPresent = () => setSearchParams({ present: '1' });
  const exitPresent = () => setSearchParams({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = gradeFilter !== 'all' ? { grade_level: gradeFilter } : {};
        const res = await axios.get(`${API}/geography`, { params });
        setFeatures(res.data.features || []);
        setStats(res.data.by_type || {});
        if (gradeFilter === '7') { setMapCenter([38.5, -98.5]); setMapZoom(7); }
        else if (gradeFilter === '10') { setMapCenter([20, 20]); setMapZoom(2); }
      } catch (e) { toast.error('Failed to load geography data'); }
      finally { setLoading(false); }
    };
    load();
  }, [gradeFilter]);

  // Play/pause timeline animation
  useEffect(() => {
    if (!isPlaying || !timelineActive) {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
      return;
    }
    const period = PERIOD_STYLES[timelineActive];
    if (!period) return;
    playIntervalRef.current = setInterval(() => {
      setCurrentYear(y => {
        const next = (y ?? period.startYear) + 1;
        if (next > period.endYear) {
          setIsPlaying(false);
          return period.endYear;
        }
        return next;
      });
    }, 900);
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    };
  }, [isPlaying, timelineActive]);

  const activateTimeline = (periodKey) => {
    if (timelineActive === periodKey) {
      // Toggle off
      setTimelineActive(null);
      setCurrentYear(null);
      setIsPlaying(false);
      // Restore world view when turning timeline off
      setMapCenter([20, 20]); setMapZoom(2);
      return;
    }
    const period = PERIOD_STYLES[periodKey];
    setTimelineActive(periodKey);
    setCurrentYear(period.startYear);
    setIsPlaying(false);
    // Snap to a sensible view for each period
    const views = {
      'Imperialism':   { center: [15, 30], zoom: 3 },
      'WWI':           { center: [48, 15], zoom: 4 },
      'WWII':          { center: [40, 30], zoom: 3 },
      'Cold War':      { center: [45, 30], zoom: 3 },
      'Post-Cold War': { center: [30, 20], zoom: 2 },
    };
    const v = views[periodKey];
    if (v) { setMapCenter(v.center); setMapZoom(v.zoom); }
  };

  const toggleType = (type) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const filtered = useMemo(() => {
    return features.filter(f => {
      if (!activeTypes.has(f.type)) return false;
      // Timeline filter: only show features active in the current year of the selected period
      if (timelineActive && currentYear !== null) {
        if (f.period !== timelineActive) return false;
        const ys = f.year_start;
        const ye = f.year_end;
        if (typeof ys === 'number' && currentYear < ys) return false;
        if (typeof ye === 'number' && currentYear > ye) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        return (f.name || '').toLowerCase().includes(q) ||
               (f.country || '').toLowerCase().includes(q) ||
               (f.continent || '').toLowerCase().includes(q) ||
               (f.description || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [features, activeTypes, search, timelineActive, currentYear]);

  const activeTypesInData = useMemo(() => {
    return Object.keys(FEATURE_STYLES).filter(k => (stats[k] || 0) > 0);
  }, [stats]);

  const handleFeatureClick = (f) => {
    setSelectedFeature(f);
    setMapCenter([f.lat, f.lng]);
    const zoomByType = {
      ocean: 3, sea: 5, trade_route: 4,
      mountain: 5, desert: 5, region: 7,
      strait: 6, landmark: 8, city: 8, capital: 6, river: 7, lake: 7,
    };
    setMapZoom(zoomByType[f.type] || 6);
  };

  if (loading) return <AppShell><div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" /></div></AppShell>;

  // Present Mode: full-screen map with just timeline controls
  if (presentMode) {
    return <PresentModeMap
      features={features}
      filtered={filtered}
      tileLayer={tileLayer}
      timelineActive={timelineActive}
      currentYear={currentYear}
      isPlaying={isPlaying}
      mapCenter={mapCenter}
      mapZoom={mapZoom}
      onActivateTimeline={activateTimeline}
      onSetIsPlaying={setIsPlaying}
      onSetCurrentYear={setCurrentYear}
      onExit={exitPresent}
      setMapCenter={setMapCenter}
      setMapZoom={setMapZoom}
    />;
  }

  return (
    <AppShell>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4 flex items-center gap-3">
          <Globe className="h-5 w-5 text-[#7A93AC]" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Resources</p>
            <h1 className="font-serif-display text-2xl text-[#14213D] tracking-tight">Interactive World Map</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={enterPresent}
              data-testid="present-mode-btn"
              title="Enter Present Mode for classroom projection"
            >
              <Maximize2 className="h-3.5 w-3.5 mr-1.5" />
              Present
            </Button>
            <Badge className="bg-[#F9F8F4] text-[#14213D] text-[11px] border border-[#EBE7E0]" data-testid="geography-feature-count">{features.length} features</Badge>
          </div>
        </div>

        {/* Grade Focus */}
        <div className="pt-1 pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-[#5A6577] font-semibold mr-1">Focus:</span>
            {GRADE_FILTERS.map(gf => (
              <button
                key={gf.key}
                onClick={() => setGradeFilter(gf.key)}
                className={`h-7 px-3 rounded text-xs font-medium transition-colors border`}
                style={gradeFilter === gf.key
                  ? { backgroundColor: gf.color, borderColor: gf.color, color: 'white' }
                  : { backgroundColor: 'white', color: '#2D3748', borderColor: '#EBE7E0' }}
                data-testid={`geo-grade-filter-${gf.key}`}
              >{gf.label}</button>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <Card className="p-3 mb-3" data-testid="timeline-card">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-[#7A93AC]" />
              <span className="text-[11px] uppercase tracking-wide text-[#5A6577] font-semibold">Timeline:</span>
            </div>
            {Object.entries(PERIOD_STYLES).map(([key, p]) => (
              <button
                key={key}
                onClick={() => activateTimeline(key)}
                className="h-7 px-3 rounded text-xs font-medium transition-colors border"
                style={timelineActive === key
                  ? { backgroundColor: p.color, borderColor: p.color, color: 'white' }
                  : { backgroundColor: 'white', color: '#2D3748', borderColor: '#EBE7E0' }}
                data-testid={`timeline-period-${key.replace(/\s+/g,'-').toLowerCase()}`}
              >{p.label}</button>
            ))}
            {timelineActive && (
              <button
                onClick={() => activateTimeline(timelineActive)}
                className="h-7 px-2 rounded text-[11px] font-medium text-stone-500 hover:text-stone-800 border border-transparent hover:border-stone-200"
                data-testid="timeline-clear"
              >Clear</button>
            )}
          </div>

          {timelineActive && (() => {
            const p = PERIOD_STYLES[timelineActive];
            const year = currentYear ?? p.startYear;
            const visibleCount = filtered.length;
            return (
              <div className="mt-3 pt-3 border-t border-[#EBE7E0]" data-testid="timeline-slider-row">
                <div className="flex items-center gap-3 mb-2">
                  <button
                    onClick={() => setIsPlaying(v => !v)}
                    className="h-8 w-8 rounded-full flex items-center justify-center text-white transition-colors"
                    style={{ backgroundColor: p.color }}
                    data-testid="timeline-play-pause"
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
                  </button>
                  <button
                    onClick={() => { setIsPlaying(false); setCurrentYear(y => Math.max(p.startYear, (y ?? p.startYear) - 1)); }}
                    className="h-7 w-7 rounded flex items-center justify-center bg-stone-100 hover:bg-stone-200 text-stone-700"
                    data-testid="timeline-step-back"
                    aria-label="Previous year"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <div className="flex-1 flex items-center gap-3">
                    <span className="text-[11px] text-stone-400 font-mono w-8 text-right">{p.startYear}</span>
                    <Slider
                      min={p.startYear}
                      max={p.endYear}
                      step={1}
                      value={[year]}
                      onValueChange={([v]) => { setIsPlaying(false); setCurrentYear(v); }}
                      data-testid="timeline-year-slider"
                      className="flex-1"
                    />
                    <span className="text-[11px] text-stone-400 font-mono w-8">{p.endYear}</span>
                  </div>
                  <button
                    onClick={() => { setIsPlaying(false); setCurrentYear(y => Math.min(p.endYear, (y ?? p.startYear) + 1)); }}
                    className="h-7 w-7 rounded flex items-center justify-center bg-stone-100 hover:bg-stone-200 text-stone-700"
                    data-testid="timeline-step-forward"
                    aria-label="Next year"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  <div
                    className="h-8 min-w-[60px] px-3 rounded flex items-center justify-center font-mono text-sm font-bold text-white"
                    style={{ backgroundColor: p.color }}
                    data-testid="timeline-current-year"
                  >{year}</div>
                </div>
                <p className="text-[11px] text-stone-500">
                  Showing {visibleCount} event{visibleCount === 1 ? '' : 's'} active in <strong>{year}</strong>. Slide, step, or press play to watch fronts and borders shift over time.
                </p>
              </div>
            );
          })()}
        </Card>

        {/* Type toggles + search */}
        <div className="pt-1 pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            {activeTypesInData.map(key => {
              const style = FEATURE_STYLES[key];
              return (
                <button key={key}
                  className={`h-7 px-3 rounded text-xs font-medium transition-colors flex items-center gap-1.5 border ${activeTypes.has(key) ? 'text-white' : 'bg-white text-stone-500 border-stone-300 opacity-60'}`}
                  style={activeTypes.has(key) ? { backgroundColor: style.color, borderColor: style.color } : {}}
                  onClick={() => toggleType(key)}
                  data-testid={`toggle-${key}`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: activeTypes.has(key) ? 'white' : style.color }} />
                  {style.label}s ({stats[key] || 0})
                </button>
              );
            })}
            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search features..."
                className="pl-8 h-7 w-48 text-xs"
                data-testid="geography-search"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="h-3 w-3 text-stone-400" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-2">
              <Card className="p-3" data-testid="continent-nav">
                <h4 className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mb-2">Navigate</h4>
                <div className="grid grid-cols-2 gap-1">
                  {CONTINENTS.map(c => (
                    <button key={c.name}
                      onClick={() => { setMapCenter([c.lat, c.lng]); setMapZoom(c.zoom); }}
                      className="text-left px-2 py-1.5 rounded text-[11px] bg-stone-50 text-stone-600 hover:bg-blue-50 hover:text-blue-800 transition-colors"
                      data-testid={`nav-${c.name.toLowerCase().replace(/\s/g,'-')}`}
                    >{c.name}</button>
                  ))}
                </div>
              </Card>

              <Card className="p-3" data-testid="map-style-controls">
                <h4 className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mb-2">Map Style</h4>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                    <button key={key} onClick={() => setTileLayer(key)}
                      className={`px-2 py-1.5 rounded text-[11px] font-medium transition-colors ${tileLayer === key ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                      data-testid={`tile-${key}`}
                    >{layer.label}</button>
                  ))}
                </div>
              </Card>

              {/* Empire Legend — visible only when Imperialism timeline is active */}
              {timelineActive === 'Imperialism' && (() => {
                // Group ALL empires (period=Imperialism) — not year-filtered — so
                // the legend is a stable color key. Then show a small "active in year"
                // count so students see which spheres are visible right now.
                const allEmpires = features.filter(f => f.type === 'empire');
                if (allEmpires.length === 0) return null;
                const visibleNamesNow = new Set(filtered.filter(f => f.type === 'empire').map(f => f.name));
                const groups = {};
                for (const e of allEmpires) {
                  const key = e.empire_group || e.name;
                  if (!groups[key]) groups[key] = { color: e.color, members: [], activeCount: 0 };
                  groups[key].members.push(e.name);
                  if (visibleNamesNow.has(e.name)) groups[key].activeCount += 1;
                }
                const GROUP_LABELS = {
                  british: 'British',
                  french: 'French',
                  russian: 'Russian',
                  ottoman: 'Ottoman',
                  japanese: 'Japanese',
                  german: 'German',
                  belgian: 'Belgian',
                  dutch: 'Dutch',
                  american: 'American',
                };
                const ordered = Object.entries(groups).sort((a, b) => b[1].members.length - a[1].members.length);
                return (
                  <Card className="p-3" data-testid="empire-legend">
                    <h4 className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mb-2">
                      Empires · {currentYear ?? PERIOD_STYLES.Imperialism.startYear}
                    </h4>
                    <div className="space-y-1">
                      {ordered.map(([groupKey, info]) => {
                        const inactive = info.activeCount === 0;
                        return (
                          <div
                            key={`legend-${groupKey}`}
                            className={`flex items-center gap-2 text-[11px] ${inactive ? 'opacity-40' : ''}`}
                            data-testid={`legend-${groupKey}`}
                          >
                            <span
                              className="w-3 h-3 rounded-sm flex-shrink-0 border border-black/10"
                              style={{ backgroundColor: info.color, opacity: 0.85 }}
                            />
                            <span className="text-stone-800 font-medium truncate flex-1">
                              {GROUP_LABELS[groupKey] || groupKey}
                            </span>
                            <span className="text-stone-500 text-[11px] tabular-nums">
                              {info.activeCount}/{info.members.length}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[9px] text-stone-400 mt-2 leading-tight">
                      Counts show polygons active this year vs. total. Advance the year to see empires appear.
                    </p>
                  </Card>
                );
              })()}

              <Card className="p-3 max-h-[50vh] overflow-y-auto" data-testid="feature-list">
                <h4 className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mb-2">
                  {search ? `Results (${filtered.length})` : `All Features (${filtered.length})`}
                </h4>
                <div className="space-y-0.5">
                  {filtered.slice(0, 200).map((f, i) => (
                    <button key={`gf-list-${f.name}-${f.lat}-${f.lng}`}
                      className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-colors flex items-center gap-2 ${selectedFeature === f ? 'bg-stone-800 text-white' : 'bg-stone-50 text-stone-700 hover:bg-stone-100'}`}
                      onClick={() => handleFeatureClick(f)}
                      data-testid={`feature-item-${i}`}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: FEATURE_STYLES[f.type]?.color }} />
                      <span className="truncate">{f.name}</span>
                    </button>
                  ))}
                </div>
              </Card>
            </div>

            {/* Map */}
            <div className="lg:col-span-4">
              <Card className="overflow-hidden" data-testid="geography-map-display">
                <div style={{ height: '600px' }} data-testid="leaflet-map">
                  <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} zoomControl={false} maxZoom={14} minZoom={2}>
                    <ZoomControl position="topright" />
                    <MapUpdater center={mapCenter} zoom={mapZoom} />
          <TileLayer url={TILE_LAYERS[tileLayer].url} attribution={TILE_LAYERS[tileLayer].attribution} crossOrigin="anonymous" />

                    {/* Empire polygons — only visible when the Imperialism timeline is active */}
                    {timelineActive === 'Imperialism' && filtered.filter(f => f.type === 'empire' && f.geometry?.coordinates?.length).map((f) => {
                      const rings = f.geometry.coordinates;
                      // Convert [lng,lat] rings to [lat,lng] for Leaflet.
                      // First ring is outer boundary; additional rings are holes.
                      const positions = rings.map(ring => ring.map(([lng, lat]) => [lat, lng]));
                      const color = f.color || '#7C2D12';
                      return (
                        <Polygon
                          key={`gf-poly-${f.name}`}
                          positions={positions}
                          pathOptions={{
                            color,
                            fillColor: color,
                            fillOpacity: 0.22,
                            weight: 1.5,
                            opacity: 0.85,
                          }}
                          eventHandlers={{ click: () => setSelectedFeature(f) }}
                        >
                          <Popup maxWidth={320}>
                            <div style={{ maxWidth: '300px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <span style={{
                                  display: 'inline-block', padding: '1px 6px', borderRadius: '4px',
                                  fontSize: '10px', fontWeight: 600,
                                  backgroundColor: color + '22', color,
                                }}>Empire</span>
                                <span style={{ fontSize: '10px', color: '#888' }}>
                                  {f.year_start}–{f.year_end}
                                </span>
                              </div>
                              <strong style={{ fontSize: '13px' }}>{f.name}</strong>
                              <p style={{ fontSize: '11px', marginTop: '4px', color: '#555', lineHeight: 1.4 }}>{f.description}</p>
                            </div>
                          </Popup>
                        </Polygon>
                      );
                    })}

                    {/* Front-line polylines — only visible during the WWII timeline */}
                    {timelineActive === 'WWII' && filtered.filter(f => f.type === 'front_line' && f.geometry?.coordinates?.length).map((f) => {
                      const isCurrentYearEvent = timelineActive && currentYear !== null
                        && f.period === timelineActive
                        && (f.year_start === currentYear || f.year_end === currentYear);
                      // Leaflet expects [lat, lng] pairs; our geometry stores [lng, lat]
                      const positions = f.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
                      const color = f.color || PERIOD_STYLES[f.period]?.color || '#8B0000';
                      return (
                        <Polyline
                          key={`gf-line-${f.name}`}
                          positions={positions}
                          pathOptions={{
                            color,
                            weight: isCurrentYearEvent ? 5 : 3.5,
                            opacity: isCurrentYearEvent ? 1 : 0.85,
                            dashArray: '6, 4',
                          }}
                          eventHandlers={{ click: () => setSelectedFeature(f) }}
                        >
                          <Popup maxWidth={320}>
                            <div style={{ maxWidth: '300px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <span style={{
                                  display: 'inline-block', padding: '1px 6px', borderRadius: '4px',
                                  fontSize: '10px', fontWeight: 600,
                                  backgroundColor: color + '20', color,
                                }}>Front Line</span>
                                {f.period && <span style={{ fontSize: '10px', color: '#888' }}>{f.period}</span>}
                              </div>
                              <strong style={{ fontSize: '13px' }}>{f.name}</strong>
                              <p style={{ fontSize: '11px', marginTop: '4px', color: '#555', lineHeight: 1.4 }}>{f.description}</p>
                            </div>
                          </Popup>
                        </Polyline>
                      );
                    })}

                    {filtered.filter(f => f.type !== 'front_line' && f.type !== 'empire').map((f) => {
                      const periodColor = f.period ? PERIOD_STYLES[f.period]?.color : null;
                      const color = periodColor || FEATURE_STYLES[f.type]?.color || '#666';
                      const radius = FEATURE_STYLES[f.type]?.radius || 4;
                      // Highlight timeline events happening exactly in the current year
                      const isCurrentYearEvent = timelineActive && currentYear !== null
                        && f.period === timelineActive
                        && (f.year_start === currentYear || f.year_end === currentYear);
                      return (
                        <CircleMarker key={`gf-marker-${f.name}-${f.lat}-${f.lng}`}
                          center={[f.lat, f.lng]}
                          radius={isCurrentYearEvent ? radius + 4 : radius}
                          pathOptions={{
                            color: color,
                            fillColor: color,
                            fillOpacity: isCurrentYearEvent ? 1 : 0.8,
                            weight: isCurrentYearEvent ? 3 : 1.5,
                          }}
                          eventHandlers={{ click: () => setSelectedFeature(f) }}
                        >
                        <Popup maxWidth={300}>
                          <div style={{ maxWidth: '280px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                              <span style={{
                                display: 'inline-block', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                                backgroundColor: (FEATURE_STYLES[f.type]?.color || '#666') + '20',
                                color: FEATURE_STYLES[f.type]?.color || '#666'
                              }}>{FEATURE_STYLES[f.type]?.label || f.type}</span>
                              {f.continent && <span style={{ fontSize: '10px', color: '#888' }}>{f.continent}</span>}
                            </div>
                            <strong style={{ fontSize: '13px' }}>{f.name}</strong>
                            {f.country && <span style={{ fontSize: '11px', color: '#888', marginLeft: '6px' }}>{f.country}</span>}
                            <p style={{ fontSize: '11px', marginTop: '4px', color: '#555', lineHeight: '1.4' }}>{f.description}</p>
                          </div>
                        </Popup>
                      </CircleMarker>
                      );
                    })}
                  </MapContainer>
                </div>

                <div className="px-3 py-2 bg-stone-50 border-t flex items-center gap-4 flex-wrap">
                  {activeTypesInData.map(key => {
                    const style = FEATURE_STYLES[key];
                    const count = filtered.filter(f => f.type === key).length;
                    if (count === 0) return null;
                    return (
                      <span key={key} className="flex items-center gap-1.5 text-[11px] text-stone-600">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: style.color }} />
                        {style.label}s ({count})
                      </span>
                    );
                  })}
                  <span className="ml-auto text-[11px] text-stone-400">{filtered.length} features shown</span>
                </div>

                {selectedFeature && (
                  <div className="p-4 bg-white border-t" data-testid="feature-detail">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: (FEATURE_STYLES[selectedFeature.type]?.color || '#666') + '15',
                            color: FEATURE_STYLES[selectedFeature.type]?.color || '#666'
                          }}>
                          {FEATURE_STYLES[selectedFeature.type]?.label || selectedFeature.type}
                        </span>
                        {selectedFeature.continent && (
                          <span className="text-[11px] text-stone-400 ml-2">{selectedFeature.continent}</span>
                        )}
                        <h4 className="text-sm font-bold text-stone-900 mt-1">
                          {selectedFeature.name}
                          {selectedFeature.country && <span className="font-normal text-stone-500 ml-2">({selectedFeature.country})</span>}
                        </h4>
                        <p className="text-xs text-stone-600 mt-1 leading-relaxed">{selectedFeature.description}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedFeature(null)} data-testid="close-feature-detail">
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}


// ─────────────────────────────────────────────────────────────────────
// PresentModeMap — classroom projection view
//   - Fullscreen map
//   - No AppShell, no sidebars, no admin chrome
//   - Big bottom timeline dock: period picker + year slider + play/pause
//   - Empire legend as compact top-left overlay
//   - ESC exits (bound in the parent)
// ─────────────────────────────────────────────────────────────────────

function PresentModeMap({
  features, filtered, tileLayer,
  timelineActive, currentYear, isPlaying,
  mapCenter, mapZoom, setMapCenter, setMapZoom,
  onActivateTimeline, onSetIsPlaying, onSetCurrentYear,
  onExit,
}) {
  const period = timelineActive ? PERIOD_STYLES[timelineActive] : null;
  const year = currentYear ?? period?.startYear;
  const [showLegend, setShowLegend] = React.useState(true);
  const [capturing, setCapturing] = React.useState(false);
  const presentRef = React.useRef(null);

  const handleFreezeFrame = React.useCallback(async () => {
    if (capturing) return;
    setCapturing(true);
    try {
      const el = presentRef.current;
      if (!el) return;
      // html-to-image lazily so it never blocks the initial map render.
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(el, {
        cacheBust: true,
        pixelRatio: 2,
        // Skip elements that are visually noise in a saved screenshot
        // (e.g. the exit button and the screenshot button itself).
        filter: (node) => !node?.dataset || node.dataset.captureIgnore !== '1',
        backgroundColor: '#0f172a',
      });
      const label = timelineActive
        ? `${timelineActive.replace(/\s+/g, '-').toLowerCase()}-${year}`
        : 'map';
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `atlas-history-${label}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Frame saved');
    } catch (err) {
      console.error('Freeze-frame failed', err);
      toast.error('Could not capture the frame — try again.');
    } finally {
      setCapturing(false);
    }
  }, [capturing, timelineActive, year]);

  // Compute empire legend groups from ALL empire features (stable color key)
  const empireLegendGroups = React.useMemo(() => {
    if (timelineActive !== 'Imperialism') return null;
    const allEmpires = features.filter(f => f.type === 'empire');
    if (allEmpires.length === 0) return null;
    const visibleNames = new Set(filtered.filter(f => f.type === 'empire').map(f => f.name));
    const groups = {};
    for (const e of allEmpires) {
      const key = e.empire_group || e.name;
      if (!groups[key]) groups[key] = { color: e.color, members: [], activeCount: 0 };
      groups[key].members.push(e.name);
      if (visibleNames.has(e.name)) groups[key].activeCount += 1;
    }
    return Object.entries(groups).sort((a, b) => b[1].members.length - a[1].members.length);
  }, [features, filtered, timelineActive]);

  const GROUP_LABELS = {
    british: 'British', french: 'French', russian: 'Russian', ottoman: 'Ottoman',
    japanese: 'Japanese', german: 'German', belgian: 'Belgian', dutch: 'Dutch', american: 'American',
  };

  return (
    <div ref={presentRef} className="fixed inset-0 z-[100] bg-[#0f172a] text-white" data-testid="present-mode">
      {/* Map fills the viewport */}
      <div className="absolute inset-0" data-testid="present-map">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%', background: '#0f172a' }}
          zoomControl={false}
          maxZoom={14}
          minZoom={2}
        >
          <ZoomControl position="topright" />
          <MapUpdater center={mapCenter} zoom={mapZoom} />
          <TileLayer url={TILE_LAYERS[tileLayer].url} attribution={TILE_LAYERS[tileLayer].attribution} crossOrigin="anonymous" />

          {/* Empire polygons */}
          {timelineActive === 'Imperialism' && filtered.filter(f => f.type === 'empire' && f.geometry?.coordinates?.length).map((f) => {
            const positions = f.geometry.coordinates.map(ring => ring.map(([lng, lat]) => [lat, lng]));
            const color = f.color || '#7C2D12';
            return (
              <Polygon
                key={`present-poly-${f.name}`}
                positions={positions}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.24, weight: 1.5, opacity: 0.9 }}
              >
                <Popup maxWidth={320}>
                  <strong style={{ fontSize: '13px' }}>{f.name}</strong>
                  <span style={{ fontSize: '10px', color: '#888', marginLeft: '6px' }}>
                    {f.year_start}–{f.year_end}
                  </span>
                  <p style={{ fontSize: '11px', marginTop: '4px', color: '#555', lineHeight: 1.4 }}>{f.description}</p>
                </Popup>
              </Polygon>
            );
          })}

          {/* Front lines */}
          {timelineActive === 'WWII' && filtered.filter(f => f.type === 'front_line' && f.geometry?.coordinates?.length).map((f) => {
            const positions = f.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
            const color = f.color || '#8B0000';
            return (
              <Polyline
                key={`present-line-${f.name}`}
                positions={positions}
                pathOptions={{ color, weight: 4, opacity: 0.9, dashArray: '6, 4' }}
              />
            );
          })}

          {/* Circle markers */}
          {filtered.filter(f => f.type !== 'front_line' && f.type !== 'empire').map((f) => {
            const periodColor = f.period ? PERIOD_STYLES[f.period]?.color : null;
            const color = periodColor || FEATURE_STYLES[f.type]?.color || '#666';
            const radius = FEATURE_STYLES[f.type]?.radius || 4;
            const isCurrent = timelineActive && currentYear != null && f.period === timelineActive
              && (f.year_start === currentYear || f.year_end === currentYear);
            return (
              <CircleMarker
                key={`present-mk-${f.name}-${f.lat}-${f.lng}`}
                center={[f.lat, f.lng]}
                radius={isCurrent ? radius + 4 : radius}
                pathOptions={{ color, fillColor: color, fillOpacity: isCurrent ? 1 : 0.85, weight: isCurrent ? 3 : 1.5 }}
              >
                <Popup maxWidth={320}>
                  <strong style={{ fontSize: '13px' }}>{f.name}</strong>
                  <p style={{ fontSize: '11px', marginTop: '4px', color: '#555', lineHeight: 1.4 }}>{f.description}</p>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {/* Top bar overlay: wordmark + legend toggle + exit */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-3 pointer-events-none z-[110]">
        <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded pointer-events-auto flex items-center gap-3">
          <Globe className="h-4 w-4 text-[#D4AF37]" />
          <span className="text-[11px] uppercase tracking-[0.28em] font-semibold">Atlas History</span>
          <span className="text-[11px] text-white/60">Present Mode</span>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto" data-capture-ignore="1">
          <button
            onClick={handleFreezeFrame}
            disabled={capturing}
            className="bg-black/60 backdrop-blur-md px-3 py-2 rounded text-xs hover:bg-black/80 transition-colors flex items-center gap-1.5 disabled:opacity-60"
            data-testid="present-freeze-frame"
            title="Save the current map view as a PNG for slides or handouts"
          >
            <Camera className="h-3.5 w-3.5" />
            {capturing ? 'Saving…' : 'Screenshot'}
          </button>
          {empireLegendGroups && (
            <button
              onClick={() => setShowLegend(v => !v)}
              className="bg-black/60 backdrop-blur-md px-3 py-2 rounded text-xs hover:bg-black/80 transition-colors flex items-center gap-1.5"
              data-testid="present-legend-toggle"
              title={showLegend ? 'Hide empire legend' : 'Show empire legend'}
            >
              {showLegend ? 'Hide Legend' : 'Show Legend'}
            </button>
          )}
          <button
            onClick={onExit}
            className="bg-black/60 backdrop-blur-md px-3 py-2 rounded text-xs hover:bg-black/80 transition-colors flex items-center gap-1.5"
            data-testid="exit-present-mode"
            title="Exit Present Mode (ESC)"
          >
            <Minimize2 className="h-3.5 w-3.5" />
            Exit
          </button>
        </div>
      </div>

      {/* Empire Legend overlay — top-left, only during Imperialism when showLegend on */}
      {empireLegendGroups && showLegend && (
        <div
          className="absolute top-20 left-6 bg-black/70 backdrop-blur-md rounded-lg p-4 z-[109] w-64 pointer-events-auto"
          data-testid="present-empire-legend"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-white/80">
              Empires · {year}
            </span>
            <span className="text-[9px] text-white/40 tabular-nums">
              {empireLegendGroups.reduce((acc, [, g]) => acc + g.activeCount, 0)} of {empireLegendGroups.reduce((acc, [, g]) => acc + g.members.length, 0)}
            </span>
          </div>
          <div className="space-y-1.5">
            {empireLegendGroups.map(([groupKey, info]) => {
              const inactive = info.activeCount === 0;
              return (
                <div
                  key={`present-legend-${groupKey}`}
                  className={`flex items-center gap-2 text-xs ${inactive ? 'opacity-40' : ''}`}
                  data-testid={`present-legend-${groupKey}`}
                >
                  <span
                    className="w-3 h-3 rounded-sm flex-shrink-0 border border-white/20"
                    style={{ backgroundColor: info.color, opacity: 0.9 }}
                  />
                  <span className="text-white font-medium truncate flex-1">
                    {GROUP_LABELS[groupKey] || groupKey}
                  </span>
                  <span className="text-white/50 text-[11px] tabular-nums">
                    {info.activeCount}/{info.members.length}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom dock: timeline controls */}
      <div className="absolute bottom-0 left-0 right-0 px-6 pb-6 pointer-events-none z-[110]">
        <div className="bg-black/70 backdrop-blur-md rounded-lg p-4 pointer-events-auto max-w-4xl mx-auto" data-testid="present-timeline-dock">
          {/* Period picker */}
          <div className="flex items-center justify-center gap-2 flex-wrap mb-3">
            {Object.entries(PERIOD_STYLES).map(([key, p]) => (
              <button
                key={key}
                onClick={() => onActivateTimeline(key)}
                className="px-4 py-1.5 rounded text-sm font-medium transition-colors border"
                style={timelineActive === key
                  ? { backgroundColor: p.color, borderColor: p.color, color: 'white' }
                  : { backgroundColor: 'transparent', color: 'white', borderColor: 'rgba(255,255,255,0.25)' }}
                data-testid={`present-period-${key.replace(/\s+/g,'-').toLowerCase()}`}
              >{p.label}</button>
            ))}
          </div>

          {timelineActive && (
            <div className="flex items-center gap-4">
              <button
                onClick={() => onSetIsPlaying(!isPlaying)}
                className="h-10 w-10 rounded-full flex items-center justify-center text-white shadow-lg"
                style={{ backgroundColor: period.color }}
                data-testid="present-play-pause"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </button>
              <div className="flex-1 flex items-center gap-3">
                <span className="text-xs text-white/60 font-mono w-10 text-right">{period.startYear}</span>
                <Slider
                  min={period.startYear}
                  max={period.endYear}
                  step={1}
                  value={[year]}
                  onValueChange={([v]) => { onSetIsPlaying(false); onSetCurrentYear(v); }}
                  className="flex-1"
                  data-testid="present-year-slider"
                />
                <span className="text-xs text-white/60 font-mono w-10">{period.endYear}</span>
              </div>
              <div
                className="h-10 min-w-[80px] px-4 rounded flex items-center justify-center font-mono text-2xl font-bold text-white shadow-lg"
                style={{ backgroundColor: period.color }}
                data-testid="present-current-year"
              >{year}</div>
            </div>
          )}

          {!timelineActive && (
            <p className="text-center text-xs text-white/60">Select a timeline period above to begin. Press ESC to exit Present Mode.</p>
          )}
        </div>
      </div>
    </div>
  );
}
